import type {Content, KeyTextField} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import {PrismicNextImage} from '@prismicio/next/pages'
import type {SliceComponentProps} from '@prismicio/react'
import {PrismicRichText} from '@prismicio/react'
import cn from 'classnames'
import {memo} from 'react'
import {useFormContext} from 'react-hook-form'
import {EVENTS, track} from 'src/common/helpers'
import type {IconType} from 'src/common/Icon'
import {Icon} from 'src/common/Icon'
import {InfoTooltip} from 'src/common/InfoTooltip'
import {Tag} from 'src/common/Tag'

/**
 * Props for `FormTile`.
 */
export type FormTileProps = SliceComponentProps<Content.FormTileSlice> & {
  context: {
    parentFormType: string
    fieldName: string
    pageUid: string
    isGrid?: boolean
    sectionLength: number
    sectionTitle?: string
    isSectionRequired: boolean
    errorMessage?: KeyTextField
    enableReskin?: boolean
    hasError?: boolean
  }
}

/**
 * Component for "FormTile" Slices.
 */
const FormTile = memo(({slice: {primary, items}, context, index}: FormTileProps): JSX.Element => {
  const value = primary.param_value || primary.title!

  const requiredMessage = context.isSectionRequired ? context.errorMessage || '' : false

  const registerOptions = {
    required: requiredMessage,
  }

  const {watch, register} = useFormContext()
  const isSelected = watch(context.fieldName).includes(value) || primary.pre_selected
  const hasIcon = isFilled.select(primary.icon) && primary.icon !== '(Empty)'
  const isReskinEnabled = context.enableReskin

  return (
    <>
      <label
        htmlFor={`${primary.title!}-input`}
        className={cn('flex items-center justify-between bg-moss-white', {
          'outline outline-[1px] -outline-offset-1': isSelected,
          'shadow-elevation-1': isSelected && !isReskinEnabled,
          'group hover:shadow-elevation-1': !primary.not_register && !isSelected && !isReskinEnabled,
          'hover:outline hover:outline-grey-150 hover:-outline-offset-1':
            !primary.not_register && !isSelected && !isReskinEnabled,
          'flex-col lg:min-h-[270px]': context.isGrid && isReskinEnabled,
          'cursor-pointer': !primary.not_register,
          'md:col-span-4 lg:flex-col lg:gap-[21px]': context.isGrid,
          'lg:gap-[30px]': !context.isGrid,
          'rounded-t-lg lg:rounded-tl-lg lg:rounded-bl-lg lg:rounded-tr-none':
            context.isGrid && index === 0 && !context.enableReskin,
          'rounded-b-lg lg:rounded-tr-lg lg:rounded-br-lg lg:rounded-bl-none':
            context.isGrid && index === context.sectionLength - 1 && !context.enableReskin,
          'outline outline-[1px] outline-moss-grey-150 -outline-offset-1':
            context.isGrid && isReskinEnabled && !isSelected,
          'p-6': !isFilled.select(primary.icon),
          'gap-2': isFilled.select(primary.icon),
          'rounded-t-lg': !context.isGrid && index === 0,
          'rounded-b-lg': !context.isGrid && index === context.sectionLength - 1,
          'outline-green-300': !context.enableReskin && isSelected,
          'outline-green-default': isReskinEnabled && isSelected,
          'outline-ui-red-300': context.hasError && context.enableReskin,
          'p-[22px] gap-[22px]': !isReskinEnabled,
          'relative rounded-lg p-4 gap-6': isReskinEnabled,
        })}
        onClick={() => {
          track({
            event: EVENTS.CLICK,
            location: `component-${index}`,
            dataType: `selector-${primary.param_value}`,
            mktgId: `${context.parentFormType?.toLowerCase()}-${isSelected ? 'deselect' : 'select'}`,
            pageName: context.pageUid,
            label: primary.title || '',
          })
        }}
      >
        {!context.isGrid && !isSelected && isReskinEnabled && (
          <svg
            className="pointer-events-none absolute inset-0 size-full text-moss-grey-200"
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="none"
          >
            <rect
              x="1"
              y="1"
              width="calc(100% - 1.5px)"
              height="calc(100% - 1.5px)"
              rx="8"
              ry="8"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5px"
              strokeDasharray="5 5"
            />
          </svg>
        )}
        <div className="flex flex-col gap-2">
          {hasIcon && (
            <div className="flex items-center gap-2">
              <Icon name={primary.icon as IconType} className="size-6 text-moss-black" />
              {isFilled.keyText(primary.tag) && (
                <Tag label={primary.tag} color={primary.tag_color} variant="small" enableReskin={isReskinEnabled} />
              )}
            </div>
          )}
          <div
            className={cn('flex flex-row-reverse items-center gap-x-6', {
              'md:flex-col-reverse md:gap-y-6 md:gap-x-0 h-full': context.isGrid,
            })}
          >
            <div className="flex size-full flex-col gap-2">
              <div
                className={cn('flex items-center gap-1.5', {
                  'lg:justify-between': context.isGrid && !isReskinEnabled,
                })}
              >
                <p
                  className={cn('text-moss-black', {
                    'text-body-bold': !isReskinEnabled,
                    'text-[18px] leading-[24px] font-medium': isReskinEnabled,
                  })}
                >
                  {primary.title}
                </p>
                {primary.tooltip_enabled && (
                  <InfoTooltip
                    id={`${index}-${primary.title?.replace(/ /g, '_')}`}
                    data-page-location={`component-${index}`}
                    data-type={`selector-${primary.param_value}`}
                    data-mktg-id="tool-tips-icon"
                    data-page-name={context.pageUid}
                    className={cn({
                      'self-end pb-1': !isReskinEnabled,
                      'self-center': isReskinEnabled,
                    })}
                  >
                    <PrismicRichText
                      field={primary.tooltip_message_rte}
                      components={{
                        paragraph: ({children}) => (
                          <p
                            className={cn('text-body-s', {
                              'text-grey-135 text-center': items.length,
                              'text-moss-white': !items.length,
                            })}
                          >
                            {children}
                          </p>
                        ),
                        heading6: ({children}) => (
                          <p className="text-body-xxs px-2 pb-2 pt-3 uppercase text-grey-150">{children}</p>
                        ),
                        list: ({children}) => <ul className="flex flex-col gap-2 px-2 pb-3">{children}</ul>,
                        listItem: ({children}) => (
                          <li className="text-body-bold-s flex items-center gap-2 align-middle text-moss-white">
                            <Icon name="check" className="size-4 min-w-4" />
                            {children}
                          </li>
                        ),
                      }}
                    />
                    {items.length > 0 && (
                      <div className="flex w-[178px] flex-wrap justify-center gap-2 px-1.5 pt-2">
                        {items.map((logo, i) => (
                          <PrismicNextImage
                            key={i}
                            width={50}
                            height={30}
                            className="max-h-[30px] max-w-[50px] self-center"
                            field={logo.tooltip_image}
                          />
                        ))}
                      </div>
                    )}
                  </InfoTooltip>
                )}
                {!hasIcon && isFilled.keyText(primary.tag) && (
                  <Tag label={primary.tag} color={primary.tag_color} variant="small" enableReskin={isReskinEnabled} />
                )}
              </div>
              {isFilled.keyText(primary.description) && (
                <p
                  className={cn('text-grey-450', {
                    'text-body-s': !isReskinEnabled,
                    'text-tg-body-m': isReskinEnabled,
                  })}
                >
                  {primary.description}
                </p>
              )}
            </div>
          </div>
        </div>
        {!primary.not_register && (
          <div
            className={cn(
              'flex size-8 shrink-0 justify-center rounded-[40px] align-middle transition-[background-color] duration-200 focus:outline-none focus:ring-2 focus:ring-blue-550',
              {
                'border-grey-150 border-[1.5px] group-hover:bg-grey-150': !isSelected && !isReskinEnabled,
                'border-moss-grey-300 border-[1.5px] group-hover:bg-moss-grey-300': !isSelected && isReskinEnabled,
                'bg-green-200': isSelected && !isReskinEnabled,
                'bg-green-default': isReskinEnabled && isSelected,
                'lg:self-start': context.isGrid,
                'self-start': isReskinEnabled,
              },
            )}
            tabIndex={0}
          >
            <Icon
              className={cn('size-5 self-center', {
                'bg-moss-grey-300 group-hover:bg-moss-white': !isSelected && !primary.not_register && isReskinEnabled,
                'bg-grey-150 group-hover:bg-moss-white': !isSelected && !primary.not_register && !isReskinEnabled,
                'bg-moss-white': isSelected,
              })}
              name={isSelected ? 'check-mark-v2' : 'plus'}
            />
          </div>
        )}
      </label>
      {!primary.not_register && (
        <input
          id={`${primary.title!}-input`}
          type={context.parentFormType.toLowerCase()}
          defaultChecked={primary.pre_selected}
          value={value}
          {...register(context.fieldName, registerOptions)}
          {...(context.parentFormType === 'Radio' && {value: primary.title!})}
          className="hidden appearance-none"
        />
      )}
    </>
  )
})

export default FormTile
